import { useState, useEffect } from 'react';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, AlertCircle, Pencil } from 'lucide-react';
import { formatCurrency } from '@/utils/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Budgets({ user, onLogout }) {
  const [budgets, setBudgets] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    category: '',
    amount: '',
    period: 'monthly'
  });

  useEffect(() => {
    fetchBudgets();
  }, []);

  const fetchBudgets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/budgets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBudgets(response.data);
    } catch (error) {
      console.error('Error fetching budgets:', error);
    }
  };

  const handleAddNew = () => {
    setEditMode(false);
    setEditingId(null);
    setFormData({ category: '', amount: '', period: 'monthly' });
    setOpen(true);
  };

  const handleEdit = (budget) => {
    setEditMode(true);
    setEditingId(budget.id);
    setFormData({
      category: budget.category,
      amount: budget.amount.toString(),
      period: budget.period
    });
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const budgetData = {
        ...formData,
        amount: parseFloat(formData.amount)
      };

      if (editMode) {
        await axios.put(
          `${API}/budgets/${editingId}`,
          budgetData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Budget updated successfully!');
      } else {
        await axios.post(
          `${API}/budgets`,
          budgetData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Budget created successfully!');
      }
      
      setOpen(false);
      setFormData({ category: '', amount: '', period: 'monthly' });
      setEditMode(false);
      setEditingId(null);
      fetchBudgets();
    } catch (error) {
      toast.error(editMode ? 'Failed to update budget' : 'Failed to create budget');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/budgets/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Budget deleted!');
      fetchBudgets();
    } catch (error) {
      toast.error('Failed to delete budget');
    }
  };

  return (
    <div className="min-h-screen p-4 lg:p-8 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto">
        <Navigation user={user} onLogout={onLogout} currentPage="/budgets" />

        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100 mb-2">Budgets</h1>
            <p className="text-gray-600 dark:text-gray-400">Set and track your spending limits</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={handleAddNew} className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Create Budget
              </Button>
            </DialogTrigger>
            <DialogContent className="dark:bg-gray-800">
              <DialogHeader>
                <DialogTitle className="dark:text-gray-100">{editMode ? 'Edit Budget' : 'Create New Budget'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="dark:text-gray-300">Category</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent container={document.body} className="dark:bg-gray-800">
                      <SelectItem value="groceries">Groceries</SelectItem>
                      <SelectItem value="dining">Dining Out</SelectItem>
                      <SelectItem value="transportation">Transportation</SelectItem>
                      <SelectItem value="entertainment">Entertainment</SelectItem>
                      <SelectItem value="utilities">Utilities</SelectItem>
                      <SelectItem value="shopping">Shopping</SelectItem>
                      <SelectItem value="healthcare">Healthcare</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="dark:text-gray-300">Amount</Label>
                  <Input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="Enter amount"
                    step="0.01"
                    required
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <Label className="dark:text-gray-300">Period</Label>
                  <Select value={formData.period} onValueChange={(value) => setFormData({ ...formData, period: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent container={document.body} className="dark:bg-gray-800">
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? (editMode ? 'Updating...' : 'Creating...') : (editMode ? 'Update Budget' : 'Create Budget')}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {budgets.length === 0 ? (
          <Card className="p-12 text-center glass dark:bg-gray-800">
            <AlertCircle className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-600 dark:text-gray-400 text-lg">No budgets set yet</p>
            <p className="text-gray-500 dark:text-gray-500 text-sm mt-2">Create your first budget to track spending</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {budgets.map((budget) => (
              <Card key={budget.id} className="p-6 glass dark:bg-gray-800 card-hover">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 capitalize">{budget.category}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 capitalize">{budget.period}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(budget)}
                      className="h-9 w-9 p-0 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(budget.id)}
                      className="h-9 w-9 p-0 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Budget</span>
                    <span className="font-semibold text-gray-800 dark:text-gray-200">{formatCurrency(budget.amount, user?.currency)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Spent</span>
                    <span className="font-semibold text-gray-800 dark:text-gray-200">{formatCurrency(budget.spent || 0, user?.currency)}</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-4">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2 rounded-full"
                      style={{ width: `${Math.min((budget.spent || 0) / budget.amount * 100, 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-500 text-right mt-1">
                    {((budget.spent || 0) / budget.amount * 100).toFixed(0)}% used
                  </p>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}